import type { Plan } from '../../motion/planner';
import type { TableModel } from '../../model/table';
import type { BoardEffect, BoardPreset, ObjectMatch } from '../types';
import type { ObjectView } from '../../model/table';
import type { BoardItem, BoardModel } from './model';
import type { Viewport } from '../geometry';

export const BOARD_MOTION = 'board-displayed-motion';
export interface DisplayedMotion { plan: Plan; previous: TableModel; next: TableModel; revision: number; reset: boolean }
export type BoardQuality = 'normal' | 'calm' | 'energy';
export interface BoardTransition { id: string; anchor: string; effect: BoardEffect; from: Viewport; to: Viewport; delay: number; duration: number; seed: number; significant: boolean }
/** The shared observed-object matcher: presets, terrain styles and piece styles all use these semantics. */
export function matchesObject(m: ObjectMatch, o: ObjectView | null | undefined) {
  return !!o && (!m.kind || m.kind === o.kind) && (!m.catalog || m.catalog.includes(o.catalogKey ?? '')) && (!m.tags || m.tags.every(tag => o.tags.includes(tag))) && (!m.fields || Object.entries(m.fields).every(([key, value]) => o.fields[key] === value));
}
export function matchesPreset(preset: BoardPreset, item: BoardItem) {
  return matchesObject(preset.match, item.object);
}
export function visualSeed(id: string, revision: number) { let seed = revision >>> 0; for (const char of id) seed = Math.imul(seed ^ char.charCodeAt(0), 16777619) >>> 0; return seed; }
/** The director's displayed plan is the only event source. No inferred identities or hidden paths. */
export function boardTransitions(plan: Plan, previous: BoardModel, next: BoardModel, revision: number): BoardTransition[] {
  if (plan.mode !== 'animate') return [];
  const transitions = new Map<string, BoardTransition>();
  for (const step of plan.steps) {
    const event = step.event; if (!('id' in event) || transitions.has(event.id)) continue;
    const item = next.items.find(i => i.object?.id === event.id), old = previous.items.find(i => i.object?.id === event.id);
    if (!item?.object) continue;
    const category = event.type === 'move' ? 'move' : event.type === 'enter' || event.type === 'owner' && !event.from && event.to ? 'arrival' : event.type === 'appearance' ? 'change' : null;
    if (!category) continue;
    const preset = next.visual?.runtime?.presets?.find(p => matchesPreset(p, item) && p[category]);
    const effect = preset?.[category]; if (!effect) continue;
    transitions.set(item.id, { id: item.id, anchor: item.anchor, effect, from: old?.rect ?? item.rect, to: item.rect, delay: Math.min(400, step.delay), duration: Math.min(800, Math.max(120, step.duration)), seed: visualSeed(item.id, revision), significant: effect === 'upgrade' || effect === 'travel' || effect === 'wood-impact' });
    if (transitions.size === 24) break;
  }
  return [...transitions.values()];
}
export const progressAt = (transition: BoardTransition, started: number, now: number) => Math.max(0, Math.min(1, (now - started - transition.delay) / transition.duration));
export const particle = (seed: number, index: number) => ((Math.imul(seed ^ (index + 1), 1103515245) + 12345) >>> 0) / 4294967296;

